import { Component, ErrorInfo, ReactNode, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { UploadStatus, CaptionStatus } from './types';
import { useAuth } from './auth';

export function AppHeader() {
  const { user, signOut } = useAuth();
  if (!user) return null;
  return (
    <header className="app-header">
      <nav>
        <NavLink to="/library">Library</NavLink>
        <NavLink to="/shared">Shared library</NavLink>
        <NavLink to="/playlists">Playlists</NavLink>
        <NavLink to="/upload">Upload</NavLink>
      </nav>
      <div>
        <span className="muted" style={{ marginRight: 12 }}>
          {[user.profile.given_name, user.profile.family_name].filter(Boolean).join(' ') ||
            user.profile.email ||
            user.profile.sub}
        </span>
        <button className="secondary" onClick={signOut}>
          Sign out
        </button>
      </div>
    </header>
  );
}

/** Fixed-size video thumbnail for list rows; renders an empty placeholder box when no thumbnail
 * is available yet (e.g. still processing) rather than collapsing the layout. */
export function Thumbnail({
  src,
  alt,
  size = 'sm',
}: {
  src?: string;
  alt: string;
  /** 'lg' for grid/tile layouts, where the thumbnail is the dominant visual element. */
  size?: 'sm' | 'lg';
}) {
  const className = size === 'lg' ? 'thumbnail thumbnail-lg' : 'thumbnail';
  if (!src) return <div className={`${className} thumbnail-placeholder`} aria-hidden="true" />;
  return <img className={className} src={src} alt={alt} loading="lazy" />;
}

/** Inline-editable `<h1>` title, used for both a personal upload's filename (detail-page.tsx) and
 * a shared-library video's title (shared-video-page.tsx) — same view/edit/save mechanics either
 * way, only `onSave` differs. */
export function EditableTitle({
  title,
  onSave,
}: {
  title: string;
  onSave: (next: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(title);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  if (!editing) {
    return (
      <h1 style={{ marginTop: 8, marginBottom: 4 }}>
        {title}{' '}
        <button
          className="secondary"
          style={{ fontSize: '0.75rem' }}
          onClick={() => {
            setName(title);
            setError(false);
            setEditing(true);
          }}
        >
          Rename
        </button>
      </h1>
    );
  }

  return (
    <form
      style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 4, alignItems: 'center' }}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim() || name.trim() === title) {
          setEditing(false);
          return;
        }
        setPending(true);
        setError(false);
        try {
          await onSave(name.trim());
          setEditing(false);
        } catch {
          setError(true);
        } finally {
          setPending(false);
        }
      }}
    >
      <input value={name} onChange={(e) => setName(e.target.value)} autoFocus style={{ flex: 1 }} />
      <button type="submit" disabled={pending}>
        Save
      </button>
      <button type="button" className="secondary" onClick={() => setEditing(false)}>
        Cancel
      </button>
      {error && <span className="error">Couldn't save.</span>}
    </form>
  );
}

/** Inline-editable description card, used for both a personal upload (detail-page.tsx) and a
 * shared-library video (shared-video-page.tsx) — same view/edit/save mechanics either way, only
 * `onSave` differs. */
export function EditableDescription({
  description,
  onSave,
}: {
  description?: string;
  onSave: (next: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(description ?? '');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);

  if (!editing) {
    return (
      <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        <h2>Description</h2>
        {description ? <p>{description}</p> : <p className="muted">No description yet.</p>}
        <button
          className="secondary"
          onClick={() => {
            setText(description ?? '');
            setError(false);
            setEditing(true);
          }}
        >
          {description ? 'Edit' : 'Add description'}
        </button>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Description</h2>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setPending(true);
          setError(false);
          try {
            await onSave(text.trim());
            setEditing(false);
          } catch {
            setError(true);
          } finally {
            setPending(false);
          }
        }}
      >
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          autoFocus
          style={{ width: '100%', resize: 'vertical' }}
        />
        <div style={{ marginTop: 'var(--space-2)', display: 'flex', gap: 8 }}>
          <button type="submit" disabled={pending}>
            Save
          </button>
          <button type="button" className="secondary" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </div>
        {error && <p className="error">Couldn't save — try again.</p>}
      </form>
    </div>
  );
}

export function StatusBadge({ status }: { status: UploadStatus }) {
  return <span className={`status-badge status-${status}`}>{label(status)}</span>;
}

function label(s: UploadStatus): string {
  switch (s) {
    case 'uploading':
      return 'Uploading';
    case 'submitted':
      return 'Submitted';
    case 'processing':
      return 'Processing';
    case 'ready':
      return 'Ready';
    case 'failed':
      return 'Failed';
  }
}

/** Absent (`undefined`) is treated the same as `'none'` — no captionStatus attribute at all means
 * generation was never attempted, same rendering either way. */
export function CaptionStatusBadge({ status }: { status?: CaptionStatus }) {
  const s = status ?? 'none';
  return <span className={`status-badge status-caption-${s}`}>{captionLabel(s)}</span>;
}

function captionLabel(s: CaptionStatus): string {
  switch (s) {
    case 'none':
      return 'No captions yet';
    case 'generating':
      return 'Generating captions…';
    case 'ready':
      return 'Captions ready';
    case 'failed':
      return "Couldn't generate captions";
    case 'unsupported':
      return 'Auto-captions unavailable for this file type';
  }
}

export function formatRelativeTime(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/**
 * Shared "share this video" UI for both a personal upload's detail page and a shared-library
 * video's detail page — same watch-link + embed-snippet mechanics either way, since both are
 * ultimately just an id passed to the public GET /watch/{id} lookup (see watch/index.ts).
 */
export function ShareAndEmbedCard({
  watchUrl,
  embedUrl,
  width,
  height,
}: {
  watchUrl: string;
  embedUrl: string;
  width?: number;
  height?: number;
}) {
  const embedSnippet = `<iframe src="${embedUrl}" width="${width ?? 640}" height="${height ?? 360}" frameborder="0" allowfullscreen></iframe>`;
  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>Share this video</h2>
      <p className="muted">
        ⚠️ Anyone with this link can view the video, no sign-in required. Don't share it for
        sensitive content.
      </p>
      <CopyRow label="Share link" value={watchUrl} />
      <div style={{ marginTop: 'var(--space-3)' }}>
        <CopyRow label="Embed code" value={embedSnippet} />
      </div>
    </div>
  );
}

export function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard write can fail in some browsers/contexts — the input is still there to select.
    }
  };
  return (
    <div>
      <div className="muted" style={{ fontSize: '0.875rem', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input readOnly value={value} style={{ flex: 1 }} onFocus={(e) => e.currentTarget.select()} />
        <button onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
      </div>
    </div>
  );
}

interface ErrorBoundaryState {
  err: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { err: null };

  static getDerivedStateFromError(err: Error): ErrorBoundaryState {
    return { err };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    console.error('Uncaught error in UI', err, info);
  }

  override render(): ReactNode {
    if (this.state.err) {
      const message = this.state.err.message ?? String(this.state.err);
      const isAccessDenied = /AccessDenied|forbidden|403/i.test(message);
      return (
        <div className="app-shell">
          <h1>Something went wrong</h1>
          {isAccessDenied ? (
            <p className="error">
              You don't have permission to do that. Try signing out and back in.
            </p>
          ) : (
            <p className="error">{message}</p>
          )}
          <button onClick={() => window.location.reload()}>Reload</button>
        </div>
      );
    }
    return this.props.children;
  }
}
